import React, { useEffect, useRef, useState, useContext } from 'react';
import { useModal } from '../Context/ModalContext';
import { SoundContext } from '../Context/SoundContext';
import crosshairYellow from '../Assets/Cursors/crosshair-yellow-64.png';
import crosshairRed from '../Assets/Cursors/crosshair-red-64.png';
import crosshairGreen from '../Assets/Cursors/crosshair-green-64.png';

const CustomCursor = ({ isActive }) => {
    // Detect pointer device (mouse/fine pointer) using matchMedia
    const [isPointerDevice, setIsPointerDevice] = useState(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        }
        return false;
    });

    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
            const handler = (e) => setIsPointerDevice(e.matches);
            mq.addEventListener('change', handler);
            return () => mq.removeEventListener('change', handler);
        }
    }, []);

    // Global mouse position tracking for accurate cursor positioning
    useEffect(() => {
        const trackGlobalMousePosition = (e) => {
            window.lastMousePosition = { x: e.clientX, y: e.clientY };
        };
        document.addEventListener('mousemove', trackGlobalMousePosition);
        document.addEventListener('click', trackGlobalMousePosition);
        return () => {
            document.removeEventListener('mousemove', trackGlobalMousePosition);
            document.removeEventListener('click', trackGlobalMousePosition);
        };
    }, []);
    const { playSoundFx } = useContext(SoundContext);
    // Remove pointer-cursor and restore crosshair when isActive changes to true
    useEffect(() => {
        if (isActive) {
            document.body.classList.remove('pointer-cursor');
            document.body.classList.remove('default-cursor');
            document.body.style.removeProperty('cursor');
            // Only re-enable if not in escape-disabled mode
            const shouldDisable = escapeDisabledRef.current ? true : false;
            isTemporarilyDisabledRef.current = shouldDisable;
            setIsTemporarilyDisabled(shouldDisable);

            // Set crosshair position to current mouse position immediately on activation
            // (e.g. after modal closes and simulator opens)
            if (typeof window !== 'undefined') {
                // Try to get mouse position from last known position or center screen
                let x = window.innerWidth / 2;
                let y = window.innerHeight / 2;
                
                // Check if we have a last known mouse position
                const lastKnownPosition = window.lastMousePosition || { x, y };
                x = lastKnownPosition.x;
                y = lastKnownPosition.y;
                
                mousePosition.current = { x, y };
                initializedRef.current = true;
            }
        }
    }, [isActive]);
    // eslint-disable-next-line no-unused-vars
    const [isTemporarilyDisabled, setIsTemporarilyDisabled] = useState(false);
    const isTemporarilyDisabledRef = useRef(false);
    // Track if Escape has been pressed and we're in forced default-cursor mode
    const escapeDisabledRef = useRef(false);
    const cursorRef = useRef(null);
    const requestRef = useRef();
    const mousePosition = useRef({ x: 0, y: 0 });
    const initializedRef = useRef(false);
    const { modal } = useModal();
    const [cursorType, setCursorType] = useState('yellow'); // 'yellow', 'red', 'green'

    // Move setInitialPosition outside useEffect so it can be referenced in cleanup
    // Set initial position to last click position when activating
    // Use document event in capture phase to guarantee this always runs
    const forceRerender = React.useReducer(x => x + 1, 0)[1];
    const setInitialPosition = React.useCallback((e) => {
        mousePosition.current = { x: e.clientX, y: e.clientY };
        initializedRef.current = true;
        // If we were in escape-disabled mode, re-enable custom cursor
        if (escapeDisabledRef.current) {
            escapeDisabledRef.current = false;
            isTemporarilyDisabledRef.current = false;
            setIsTemporarilyDisabled(false);
            document.body.classList.remove('default-cursor');
            document.body.style.removeProperty('cursor');
            forceRerender();
        }
        document.removeEventListener('click', setInitialPosition, true);
    }, [forceRerender]);

    useEffect(() => {
        if (!isActive) {
            initializedRef.current = false;
            return;
        }
        if (!isTemporarilyDisabledRef.current) {
            document.body.classList.remove('default-cursor');
        } else {
            document.body.classList.add('default-cursor');
        }

        // Mouse move handler for position and hover detection
        const handleMouseMove = (e) => {
            if (escapeDisabledRef.current) return;
            mousePosition.current = { x: e.clientX, y: e.clientY };
            let cursor = 'yellow';
            const element = document.elementFromPoint(e.clientX, e.clientY);
            const isNavHover = element && (
                element.classList.contains('logo') ||
                element.classList.contains('nav-link-left') ||
                element.classList.contains('nav-link-center') ||
                element.classList.contains('mute-button-header') ||
                element.classList.contains('mute-nav-link') ||
                (
                    element.classList.contains('nav-link') &&
                    !element.classList.contains('nav-link-left') &&
                    !element.classList.contains('nav-link-center')
                )
            );
            if (isNavHover) {
                if (!isTemporarilyDisabledRef.current) {
                    isTemporarilyDisabledRef.current = true;
                    setIsTemporarilyDisabled(true);
                }
                document.body.classList.add('pointer-cursor');
                document.body.classList.remove('default-cursor');
                document.body.style.removeProperty('cursor');
            } else {
                if (isTemporarilyDisabledRef.current) {
                    isTemporarilyDisabledRef.current = false;
                    setIsTemporarilyDisabled(false);
                }
                document.body.classList.remove('pointer-cursor');
                document.body.classList.remove('default-cursor');
                document.body.style.removeProperty('cursor');
                if (element) {
                    if (element.classList.contains('prawn') || element.classList.contains('dead-prawn')) {
                        cursor = 'red';
                    } else if (element.classList.contains('invasive') || element.classList.contains('speaker-icon-sprite') || element.classList.contains('book-icon-sprite') || element.classList.contains('eye-icon-sprite') || element.classList.contains('labsy-sprite')) {
                        cursor = 'green';
                    }
                }
            }
            setCursorType(cursor);
        };

        // Animation for cursor position
        const animateCursor = () => {
            if (cursorRef.current) {
                cursorRef.current.style.transform = 
                    `translate(${mousePosition.current.x}px, ${mousePosition.current.y}px) translate(-50%, -50%)`;
            }
            requestRef.current = requestAnimationFrame(animateCursor);
        };

        // Escape disables custom cursor until next click
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                escapeDisabledRef.current = true;
                isTemporarilyDisabledRef.current = true;
                setIsTemporarilyDisabled(true);
                document.body.classList.add('default-cursor');
                document.body.style.setProperty('cursor', 'default', 'important');
                document.addEventListener('click', setInitialPosition, true);
            }
        };

        // Click handler: play laser and set position
        const handleClick = (e) => {
            // Always remove default cursor and pointer-cursor on click
            document.body.classList.remove('default-cursor');
            document.body.classList.remove('pointer-cursor');
            document.body.style.removeProperty('cursor');
            // Prevent laser sound for header nav elements
            const element = document.elementFromPoint(e.clientX, e.clientY);
            const isNavElement = element && (
                element.classList.contains('logo') ||
                element.classList.contains('nav-link-left') ||
                element.classList.contains('nav-link-center') ||
                element.classList.contains('mute-button-header') ||
                element.classList.contains('mute-nav-link') ||
                (
                    element.classList.contains('nav-link') &&
                    !element.classList.contains('nav-link-left') &&
                    !element.classList.contains('nav-link-center')
                )
            );
            if (
                !modal.isOpen &&
                !escapeDisabledRef.current &&
                !document.body.classList.contains('pointer-cursor') &&
                isActive &&
                !isNavElement
            ) {
                playSoundFx && playSoundFx('laser');
            }
            setInitialPosition(e);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('click', handleClick, true);
        requestRef.current = requestAnimationFrame(animateCursor);

        // If no click yet, fallback to center of screen
        if (!initializedRef.current) {
            mousePosition.current = {
                x: window.innerWidth / 2,
                y: window.innerHeight / 2
            };
        }

        // Cleanup
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClick, true);
            document.body.classList.remove('default-cursor');
            cancelAnimationFrame(requestRef.current);
        };
    }, [isActive, setInitialPosition, modal.isOpen, playSoundFx]);


    // Only render if modal is not open, isActive, not temporarily disabled, not pointer-cursor, not escape-disabled, and IS pointer device
    if (!isPointerDevice || modal.isOpen || !isActive || isTemporarilyDisabledRef.current || document.body.classList.contains('pointer-cursor') || escapeDisabledRef.current) return null;

    // Choose crosshair image based on cursorType
    let crosshairUrl = crosshairYellow;
    if (cursorType === 'red') {
        crosshairUrl = crosshairRed;
    } else if (cursorType === 'green') {
        crosshairUrl = crosshairGreen;
    }

    return (
        <div
            ref={cursorRef}
            className="custom-crosshair"
            style={{
                position: 'fixed',
                width: '64px',
                height: '64px',
                pointerEvents: 'none',
                zIndex: 2147483647, // max z-index for visibility
                background: `url(${crosshairUrl}) center/contain no-repeat`,
            }}
        />
    );
// End of CustomCursor.js
}

export default CustomCursor;
